import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, NavLink, useNavigate } from "react-router-dom";
import StudentDiwanLogo from "../../Assets/HomeAssets/StudentDiwanLogo.png";
import smallLogo from "../../Assets/HomeAssets/smallLogo.png";
import sidebarData from "./DataFile/sidebarData.js";
import {
  MdOutlineKeyboardArrowUp,
  MdOutlineKeyboardArrowDown,
} from "react-icons/md";
import { FiLogOut } from "react-icons/fi";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { useDispatch, useSelector } from "react-redux";
import { staffLogout } from "../../Store/Slices/Common/Auth/actions/staffActions";
import LogoutConfirmationModal from "../Common/LogoutConfirmationModal.js";
import profileIcon from "../../Assets/DashboardAssets/profileIcon.png";
import { toggleSidebar } from "../../Store/Slices/Common/User/reducers/userSlice.js";
import { useTranslation } from "react-i18next";
import { filterSidebarData } from "../../Utils/sidebarUtils.js";
import { Tooltip } from "antd";
import "antd/dist/reset.css";
import { motion, AnimatePresence } from "framer-motion";

function useWindowSize() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    function handleResize() { setWidth(window.innerWidth); }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return { width, isMobile: width < 768 };
}

const submenuVariants = {
  open:   { opacity: 1, height: "auto", transition: { duration: 0.2 } },
  closed: { opacity: 0, height: 0,      transition: { duration: 0.2 } },
};

const isActivePath = (path, locationPath) => locationPath.startsWith(path);

const SideMenubar = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("admSidebar");

  const isOpen      = useSelector((state) => state.common.user.sidebar.isOpen);
  const role        = useSelector((state) => state.common.auth.role);
  const permissions = useSelector((state) => state.common.auth.permissions);
  const userDetails = useSelector((state) => state.common.user.userDetails);
  const selectedLanguage = useSelector((store) => store.common.auth.selectedLanguage);
  const isRTL = ["ar", "ur"].includes(selectedLanguage);

  const [openItems, setOpenItems] = useState([]);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const { isMobile } = useWindowSize();

  useEffect(() => {
    if (isMobile && isOpen) dispatch(toggleSidebar());
  }, [isMobile, isOpen, dispatch]);

  const toggleDropdown = useCallback((title) => {
    setOpenItems((prev) =>
      prev.includes(title) ? prev.filter((i) => i !== title) : [...prev, title]
    );
  }, []);

  const handleLogout = useCallback(() => setIsLogoutModalOpen(true), []);

  const confirmLogout = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      await dispatch(staffLogout()).unwrap();
      setIsLogoutModalOpen(false);
      navigate("/stafflogin");
    } finally {
      setIsLoggingOut(false);
    }
  }, [dispatch, navigate]);

  const HandleNavigate = useCallback(() => {
    if (role === "admin") navigate("/users/admin");
    else if (["teacher","finance","librarian","staff","student","parent"].includes(role))
      navigate("/users/my/profile");
  }, [role, navigate]);

  const filteredSidebarData = useMemo(() => {
    if (!sidebarData || !Array.isArray(sidebarData)) return [];
    return filterSidebarData(sidebarData, role, permissions);
  }, [role, permissions]);

  useEffect(() => {
    const initialOpenItems = [];
    filteredSidebarData.forEach((item) => {
      if (item.items) {
        const hasActiveChild = item.items.some((subItem) =>
          isActivePath(subItem.path, location.pathname)
        );
        if (hasActiveChild) initialOpenItems.push(item.title);
      }
    });
    setOpenItems(initialOpenItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!sidebarData || !Array.isArray(sidebarData)) {
    return (
      <div className="flex items-center justify-center bg-red-50 text-red-500 w-64 p-4">
        {t("ERROR_SIDEBAR_DATA_NOT_FOUND")}
      </div>
    );
  }

  // Nav item style — matching screenshot (no bg by default, purple on active)
  const navItemCls = (isActive) =>
    `flex items-center gap-3 px-3 py-2 rounded-xl  font-medium transition-all duration-150 cursor-pointer ${
      isActive
        ? "bg-purple-50 text-purple-600 border-l-2 border-purple-500"
        : "text-slate-500 hover:text-slate-700 hover:bg-gray-50"
    }`;

  return (
    <nav
      className={`fixed top-0 ${isRTL ? "right-0 border-l" : "left-0 border-r"} bottom-0 z-30 flex flex-col bg-white border-gray-100 transition-all duration-300 ${
        isOpen ? "w-[15%]" : "w-[7%]"
      }`}
      aria-label="Sidebar"
    >
      {/* ── Logo ──────────────────────────────────── */}
      <div className="relative flex items-center justify-center border-b border-gray-100 h-14 px-3">
        <NavLink to="/dashboard" aria-label="Dashboard">
          <img
            src={isOpen ? StudentDiwanLogo : smallLogo}
            alt="Logo"
            className={`transition-all duration-300 object-contain ${isOpen ? "h-9 w-36" : "h-8 w-8"}`}
          />
        </NavLink>
        <Tooltip title={isOpen ? t("Collapse Sidebar") : t("Expand Sidebar")} placement="bottom">
          <button
            onClick={(e) => { e.stopPropagation(); dispatch(toggleSidebar()); }}
            className="absolute -end-3.5 bottom-[-14px] z-50"
            aria-label={isOpen ? t("Collapse Sidebar") : t("Expand Sidebar")}
          >
            <div className="p-1 rounded-full text-purple-500 bg-white border-2 border-gray-200 shadow-sm">
              {isOpen ? <IoIosArrowBack size={12} /> : <IoIosArrowForward size={12} />}
            </div>
          </button>
        </Tooltip>
      </div>

      {/* ── Menu Items ────────────────────────────── */}
      <div className="flex-grow overflow-y-auto py-3 px-2">
        <ul className="space-y-0.5">
          {filteredSidebarData.map((item, index) => (
            <React.Fragment key={item.title}>
              {item.items ? (
                // Parent with submenu
                <div>
                  <Tooltip placement="right" title={!isOpen ? t(item.title) : ""} trigger={["hover"]} overlayClassName="!bg-gray-700 !text-white">
                    <div
                      className={`flex items-center cursor-pointer mt-3 mb-1 px-2 py-1 rounded-xl transition-colors hover:bg-gray-50 ${
                        !isOpen ? "justify-center" : "justify-between"
                      }`}
                      onClick={() => toggleDropdown(item.title)}
                      role="button"
                      aria-expanded={openItems.includes(item.title)}
                      aria-controls={`submenu-${index}`}
                      tabIndex={0}
                      onKeyDown={(e) => e.key === "Enter" && toggleDropdown(item.title)}
                    >
                      {isOpen ? (
                        <>
                          <span className="text-[12px] font-bold text-gray-400 uppercase tracking-wider">
                            {t(item.title)}
                          </span>
                          {openItems.includes(item.title)
                            ? <MdOutlineKeyboardArrowUp className="text-gray-400 text-base flex-shrink-0" />
                            : <MdOutlineKeyboardArrowDown className="text-gray-400 text-base flex-shrink-0" />
                          }
                        </>
                      ) : (
                        <span className={`text-xl transition-colors ${
                          item.items.some((sub) => isActivePath(sub.path, location.pathname))
                            ? "text-purple-500"
                            : "text-gray-400"
                        }`}>
                          {item.icon}
                        </span>
                      )}
                    </div>
                  </Tooltip>

                  {/* Submenu */}
                  <AnimatePresence initial={false}>
                    {openItems.includes(item.title) && (
                      <motion.ul
                        key={item.title}
                        id={`submenu-${index}`}
                        variants={submenuVariants}
                        initial="closed"
                        animate="open"
                        exit="closed"
                        className="space-y-0.5 overflow-hidden"
                      >
                        {item.items
                          .filter((subItem) =>
                            subItem.roles.includes(role) &&
                            (!subItem.requiredPermission || permissions.includes(subItem.requiredPermission))
                          )
                          .map((subItem) => (
                            <li key={subItem.title}>
                              <Tooltip placement="right" title={!isOpen ? t(subItem.title) : ""} trigger={["hover"]} overlayClassName="!bg-gray-700 !text-white">
                                <NavLink
                                  to={subItem.path}
                                  aria-label={t(subItem.title)}
                                  className={({ isActive }) =>
                                    navItemCls(isActive || isActivePath(subItem.path, location.pathname)) +
                                    (isOpen ? "" : " justify-center px-0 py-3")
                                  }
                                >
                                  <span className="text-[1.1rem] text-slate-400 flex-shrink-0">{subItem.icon}</span>
                                  {isOpen && (
                                    <span className={`text-[14px] ${isRTL ? "pe-2" : ""}`}>
                                      {t(subItem.title)}
                                    </span>
                                  )}
                                </NavLink>
                              </Tooltip>
                            </li>
                          ))}
                      </motion.ul>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                // Standalone item
                <li className="mb-8 mt-2">
                  <Tooltip placement="right" title={!isOpen ? t(item.title) : ""} trigger={["hover"]} overlayClassName="!bg-gray-700 !text-white">
                    <NavLink
                      to={item.path}
                      aria-label={t(item.title)}
                      className={({ isActive }) =>
                        navItemCls(isActive || isActivePath(item.path, location.pathname)) +
                        (isOpen ? "" : " justify-center px-0 py-2")
                      }
                    >
                      <span className={`${!isOpen ? "text-xl" : "text-[1.15rem]"} text-slate-400 flex-shrink-0`}>
                        {item.icon}
                      </span>
                      {isOpen && (
                        <span className={`text-[14px] ${isRTL ? "pe-2" : ""}`}>
                          {t(item.title)}
                        </span>
                      )}
                    </NavLink>
                  </Tooltip>
                </li>
              )}
            </React.Fragment>
          ))}
        </ul>
      </div>

      {/* ── Profile + Logout ──────────────────────── */}
      <div className="px-3 py-3 flex items-center gap-2.5 border-t border-gray-100">
        <Tooltip placement="right" title={!isOpen ? "Profile" : ""} trigger={["hover"]} overlayClassName="!bg-gray-700 !text-white">
          <img
            src={userDetails?.profile || profileIcon}
            alt="Profile"
            className="w-7 h-7 cursor-pointer rounded-full object-cover flex-shrink-0 border border-gray-200"
            onClick={HandleNavigate}
          />
        </Tooltip>

        {isOpen && (
          <div className="flex-1 min-w-0">
            <p className="font-bold text-[12px] text-gray-800 truncate leading-tight m-1">
              {userDetails?.fullName
                ? userDetails.fullName.split(" ").map((n) => n[0]?.toUpperCase()).join("")
                : "User"}
            </p>
            <p className="text-gray-400 capitalize text-[10px] truncate leading-tight m-1">{role}</p>
          </div>
        )}

        <Tooltip placement="right" title={!isOpen ? t("Logout") : ""} trigger={["hover"]} overlayClassName="!bg-gray-700 !text-white">
          <button
            onClick={handleLogout}
            className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
            aria-label={t("Logout")}
          >
            <FiLogOut size={15} />
          </button>
        </Tooltip>
      </div>

      <LogoutConfirmationModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={confirmLogout}
        loading={isLoggingOut}
      />
    </nav>
  );
};

export default React.memo(SideMenubar);